import { Helmet } from 'react-helmet-async';
import { SectionCards } from '@/components/section-cards';
import { ChartAreaInteractive } from '@/components/chart-area-interactive';
import { DataTable } from '@/components/data-table';
import { PermissionGate } from '@/components/auth/PermissionGate';
import { useAuth } from '@/hooks/useAuth';

export function Dashboard() {
  const { user } = useAuth();

  // Get organization-specific venue data
  const getVenueData = () => {
    if (!user) return [];

    if (user.role.includes('super_admin')) {
      // Super admin sees all venues
      return [
        {
          id: 1,
          header: 'Downtown Venue',
          type: 'Restaurant',
          status: 'Done',
          target: '100',
          limit: '150',
          reviewer: 'Eddie Lake',
        },
        {
          id: 2,
          header: 'Rooftop Bar',
          type: 'Bar',
          status: 'In Progress',
          target: '75',
          limit: '100',
          reviewer: 'Assign reviewer',
        },
        {
          id: 3,
          header: 'City Center Cafe',
          type: 'Cafe',
          status: 'Done',
          target: '50',
          limit: '75',
          reviewer: 'Sarah Chen',
        },
      ];
    }

    if (user.role.includes('venue_manager')) {
      // Venue manager sees only their organization's venues
      return [
        {
          id: 1,
          header: 'Urban Coffee Downtown',
          type: 'Cafe',
          status: 'Done',
          target: '45',
          limit: '60',
          reviewer: 'Mike Johnson',
        },
        {
          id: 2,
          header: 'Urban Coffee Uptown',
          type: 'Cafe',
          status: 'In Progress',
          target: '30',
          limit: '45',
          reviewer: 'Lisa Wong',
        },
      ];
    }

    // Viewer sees limited venue info
    return [
      {
        id: 1,
        header: 'Your Assigned Venue',
        type: 'Cafe',
        status: 'Viewing',
        target: 'N/A',
        limit: 'N/A',
        reviewer: 'Read Only',
      },
    ];
  };

  const venueData = getVenueData();

  return (
    <>
      <Helmet>
        <title>Dashboard - Urban Finch Admin</title>
      </Helmet>
      <div data-testid="dashboard-page">
        <div className="space-y-2" data-testid="dashboard-header">
          <h1 className="text-2xl font-semibold tracking-tight lg:text-3xl">
            Dashboard
          </h1>
          <p className="text-muted-foreground">
            Welcome back! Here's an overview of your business.
          </p>
        </div>

        <div data-testid="dashboard-content">
          {/* Metrics Cards - Always visible */}
          <SectionCards />

          {/* Analytics Chart - Role-based access */}
          <PermissionGate permission="system_management:read">
            <div className="space-y-4" data-testid="analytics-section">
              <h2 className="text-lg font-medium">Analytics Overview</h2>
              <ChartAreaInteractive data-testid="analytics-chart" />
            </div>
          </PermissionGate>

          {/* Data Table - Role-based access */}
          <PermissionGate permission="venue_management:read">
            <div className="space-y-4" data-testid="venues-section">
              <h2 className="text-lg font-medium">
                {user?.role.includes('super_admin')
                  ? 'All Venues'
                  : user?.role.includes('venue_manager')
                    ? `${user?.organization?.name || 'Organization'} Venues`
                    : 'Your Venues'}
              </h2>
              <DataTable data-testid="venues-table" data={venueData} />
            </div>
          </PermissionGate>
        </div>
      </div>
    </>
  );
}
